import {
  faqData,
  programs,
  moreFaqData,
  normalPrograms,
} from "LandingPage/BrandNewLandingPage/commonData";
import { Form, message } from "antd";
import { isVoid } from "utils";
import { mergeWith } from "lodash";
import { getValue } from "./utils";
import { useRequest } from "ahooks";
import { request } from "../request";
import { getCustomerId } from "Common";
import React, { useEffect, useState } from "react";
import EditPreview from "./EditPreview";
import { STATE_CAPABILITIES_FIELD, useAsyncUpdate } from "./hooks";
import ProfileConfig from "./ProfileConfig";
import Container from "./components/Container";
import { parseAddress } from "components/Address/Select";
import {
  normalizeStateCapabilities,
  stateCodesFromCapabilities,
} from "./stateCapabilities";
import { ORG_SWITCH_NOTICE_KEY } from "../BranchSwitcher/switchOrganization";
import { track } from "statsig";
import { STATSIG, PAGE } from "statsig/events";

import { ENUM_PERSONAL_SITE } from "./enum";
import { PERSONAL_ITEMS } from "./ProfileConfig";

import type { TypeProfileInfo } from "./interface";

export type TypeResponseData = TypeProfileInfo.DTO & {
  company: TypeProfileInfo.Company;
};

const CUSTOMER_ID = getCustomerId();
const IS_GMCC = CUSTOMER_ID === "gmcc";

const FAQ_DEFAULT = [...faqData, ...moreFaqData];

// 只有 “待审核”、“审核已通过” 才需要重新发起审核
export const RE_EXAMINE_STATUS = [
  ENUM_PERSONAL_SITE.STATUS.WAIT,
  ENUM_PERSONAL_SITE.STATUS.ACTIVATED,
];

// 目前主要是针对此类客户有定制化的内容
export const IS_CUSTOMIZED_CUSTOMER = ["googain", "gmcc"].includes(
  getCustomerId(),
);

// 是否显示 “Mortgage Rate Calculator” 、 “Affordability Calculator” 功能
export const SHOW_WIDGETS_FEATURES = !IS_CUSTOMIZED_CUSTOMER;

/**
 * @name BlueRatePersonalSite 配置用户 “Bluerate“、“Personal site”
 * @description 之前 “Bluerate“、“Personal site” 是两个功能，现在全部合并在一起
 *              代码逻辑参照 InitialSetup > NewPreviewLandingPageSetup.js > getLoanOfficerProfile cyberpunk code
 */
const BlueRatePersonalSite = () => {
  const [form] = Form.useForm<TypeProfileInfo.DTO>();

  const [process, setProcess] = useState<number | void>(); // 用户配置流程位置

  useEffect(() => {
    track(STATSIG.COMMON.VIEW, PAGE.PERSONAL_SITE_BLUERATE, { page: PAGE.PERSONAL_SITE_BLUERATE });
  }, []);

  useEffect(() => {
    const notice = sessionStorage.getItem(ORG_SWITCH_NOTICE_KEY);
    if (!notice) return;
    sessionStorage.removeItem(ORG_SWITCH_NOTICE_KEY);
    message.success(notice);
  }, []);

  // 审核状态
  const { data: status, runAsync: getStatus } = useRequest(
    async () => (await request("/los/getverifystatus"))?.status,
    { pollingInterval: 300000, manual: true },
  );

  // 更新数据
  const { sync, event, setServerData } = useAsyncUpdate(
    async (body, signal, reAudit, changedFields) => {
      await request("/los/updateprofiledraft", {
        body,
        signal,
        method: "POST",
        isSuccess: (res) => res.status === 200,
      });
      await request(`/los/updateprofiledraft`, {
        signal,
        body: {},
        method: "POST",
        isSuccess: (res) => res.status === 200,
      });
      if (reAudit && RE_EXAMINE_STATUS.includes(status)) {
        // 用户若更改关键字段，需要重新发起审核
        await request("/los/setverifystatus", {
          method: "POST",
          body: { nmls: body.NMLS, status: ENUM_PERSONAL_SITE.STATUS.WAIT },
        });
        getStatus();
      }
    },
  );

  // 获取数据
  const { data, loading, runAsync } = useRequest(async () => {
    let [profile, company, status] = await Promise.all([
      request("/los/getprofilesetup"),
      request("/los/getcompanyprofilesetup"),
      getStatus(),
    ]);
    if (
      // 如果用户从未发布过，但是有草稿记录，那么就需要合并下
      status === ENUM_PERSONAL_SITE.STATUS.UN_INIT &&
      profile?.LandingPageSetUp?.draft
    ) {
      const backup = JSON.parse(JSON.stringify(profile));
      const draft = backup.LandingPageSetUp.draft;
      backup.LandingPageSetUp.draft = undefined;
      profile = mergeWith({}, backup, draft, (a, b) =>
        isVoid(b) ? a : undefined,
      );
    }
    const landing = profile.LandingPageSetUp;
    landing.StateCapabilities = normalizeStateCapabilities(
      landing.StateCapabilities,
      landing.States,
    );
    landing.States = stateCodesFromCapabilities(landing.StateCapabilities);
    landing.LoanTypes = landing.LoanTypes?.length
      ? landing.LoanTypes
      : ["Conventional"]; // 自动审核校验必要参数，初始化给个默认值
    landing.Email = landing?.Email || profile?.Email;
    landing.CompanyName = landing?.CompanyName || company?.Name;
    landing.CompanyAddress = company?.Address || landing.CompanyAddress;
    landing.CompanyZip = company?.Zip || landing.CompanyZip;
    landing.CompanyCity = company?.City || landing.CompanyCity;
    landing.CompanyState = company?.State || landing.CompanyState;
    landing.CompanyCounty = company?.County || landing.CompanyCounty;
    landing.companyLink = landing?.companyLink || company?.Link;
    landing.banner = landing?.banner || company?.Banner;
    landing.companyIntroduction =
      landing.companyIntroduction || company.companyIntroduction;
    const params = landing?.paramsInfo;
    landing.paramsInfo = {
      title:
        params?.title ||
        "How Do I Empower You to Build Your Solid Financial Foundation?",
      paramsList: params?.paramsList || (IS_GMCC ? programs : normalPrograms),
    };
    // 可能会有空的情况？手动生成一个
    if (!profile?.LandingUrl) {
      profile.LandingUrl = `/${CUSTOMER_ID}/${window.__U.firstName}`;
    }
    if (!landing?.faqInfo) {
      landing.faqInfo = FAQ_DEFAULT;
    }
    // 是否显示 “Company” 入口
    if (IS_CUSTOMIZED_CUSTOMER && !company.ShowCompanyInfo) {
      const INDEX_COMPANY = PERSONAL_ITEMS.findIndex(
        (v) => v.title === "Company details",
      );
      ~INDEX_COMPANY && PERSONAL_ITEMS.splice(INDEX_COMPANY, 1);
    }
    // 现在没有draft这个概念了
    if (landing?.draft) {
      landing.draft = undefined;
    }
    // 有些帐户只有CompanyAddress，但是没有对应的zip、city、state、county，解析生成一次
    if (
      landing.CompanyAddress &&
      !(
        landing.CompanyCounty &&
        landing.CompanyZip &&
        landing.CompanyCity &&
        landing.CompanyState
      )
    ) {
      const address = await parseAddress(landing.CompanyAddress);
      landing.CompanyZip = landing.CompanyZip || address?.zip;
      landing.CompanyCity = landing.CompanyCity || address?.city;
      landing.CompanyState = landing.CompanyState || address?.state;
      landing.CompanyCounty = landing.CompanyCounty || address?.county;
    }
    setProcess(getProcess(profile));
    form?.setFieldsValue?.(profile);
    setServerData(profile);
    return { ...profile, company };
  });

  /**
   * @name getProcess 获取用户 初始配置状态
   * @description 哪个步骤有没填写的，就直接跳过去让用户填写
   */
  function getProcess(profile: TypeProfileInfo.DTO) {
    if (!profile?.Image) return -1;
    for (let i = 0; i < PERSONAL_ITEMS.length; i++) {
      if (
        Object.values(PERSONAL_ITEMS[i].validate).filter((v) =>
          isVoid(getValue(profile, v)),
        )?.length
      ) {
        return i;
      }
    }
  }

  return (
    <Container loading={!data}>
      <Form form={form} layout="vertical" {...event}>
        {Number.isFinite(process) ? (
          <ProfileConfig loading={sync} value={process} onChange={setProcess} />
        ) : (
          <EditPreview
            data={data}
            sync={sync}
            status={status}
            loading={loading}
            getStatus={getStatus}
            onUpdateData={runAsync}
          />
        )}
      </Form>
    </Container>
  );
};

export default BlueRatePersonalSite;
